/** Fail-closed, live staff picker for the member header's coach action (DNKADM-0301). */
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { useStaffPickList } from '@/api/staff';
import { PickListLoader } from '@/components/PickListLoader';
import { avatarFor, Icon, initialsOf, resolveTokens } from '@/theme';

export function CoachPickerDialog({
  currentStaffId,
  assigned,
  busy,
  error,
  canRetry,
  onClose,
  onAssign,
  onRemove,
  onRetry,
}: {
  currentStaffId: string | null;
  assigned: boolean;
  busy: boolean;
  error: string | null;
  canRetry: boolean;
  onClose: () => void;
  onAssign: (staffId: string) => void;
  onRemove: () => void;
  onRetry: () => void;
}) {
  // The dialog is conditionally mounted by MemberDetail, so each open loads a
  // fresh active-staff list. A stale-staffer rejection remounts it as well.
  const staff = useStaffPickList();
  const theme = useTheme();
  const t = resolveTokens(theme);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="coach-picker-title">
      <DialogTitle
        id="coach-picker-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="sports" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Set assigned coach</Typography>
          <Typography variant="body2">Pick an active staff member for this member.</Typography>
        </Box>
        <IconAction size="small" onClick={onClose} label="Close" disabled={busy}>
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            role="alert"
            action={
              canRetry ? (
                <Button color="inherit" size="small" onClick={onRetry} disabled={busy}>
                  Retry
                </Button>
              ) : undefined
            }
            sx={{ mb: 1.5 }}
          >
            {error}
          </Alert>
        )}
        <PickListLoader list={staff} label="the staff list">
          {(members) =>
            members.length === 0 ? (
              <Typography variant="body2" sx={{ color: t.text2, py: 1 }}>
                No active staff members are available to assign.
              </Typography>
            ) : (
              <List disablePadding>
                {members.map((member) => {
                  const isCurrent = member.id === currentStaffId;
                  return (
                    <ListItemButton
                      key={member.id}
                      onClick={() => onAssign(member.id)}
                      disabled={busy || isCurrent}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            fontSize: 15,
                            bgcolor: avatarFor(member.id, theme.palette.mode).bg,
                            color: avatarFor(member.id, theme.palette.mode).fg,
                          }}
                        >
                          {initialsOf(member.name)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={isCurrent ? `${member.name} (current)` : member.name}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )
          }
        </PickListLoader>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        {assigned && (
          <Button
            color="inherit"
            startIcon={<Icon name="person_remove" />}
            onClick={onRemove}
            disabled={busy}
            sx={{ mr: 'auto', color: t.text2 }}
          >
            Remove coach
          </Button>
        )}
        <Button color="inherit" onClick={onClose} disabled={busy} sx={{ color: t.text2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
